'use client'

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import { isHsrpLandingWidget } from '@helpers/vasLandingPageHelper'
import {
  MOBILE_NUMBER_LOCK,
  PHONE_NUMBER_PREFIX,
  ENTER_VALID_MOBILE_NUMBER,
  ENTER_MOBILE_NUMBER,
  BUTTON_CTA,
  ENTER_INFO_MAPPING,
  DESCRIPTION_MAPPING,
  PHONE_NUMBER_INFO,
} from './constants'
import { validateMobile } from './utils'
import styles from './mobileNumberInputModal.module.scss'

interface MobileNumberModalProps {
  isMobile: boolean
  onSubmit?: (mobileNumber: string) => void
  showMobileNumberModal?: boolean
  setShowMobileNumberModal: Function
  widgetType: string
}

const MobileNumberModal: React.FC<MobileNumberModalProps> = ({
  onSubmit,
  isMobile,
  showMobileNumberModal,
  setShowMobileNumberModal,
  widgetType,
}) => {
  const [mobileNo, setMobileNo] = useState<string>('')
  const [error, setError] = useState<string>('')
  const modalRef = useRef<HTMLDivElement>(null)
  const isHsrpPage = isHsrpLandingWidget(widgetType)

  useEffect(() => {
    if (showMobileNumberModal) {
      document.documentElement.style.overflowY = 'hidden'
    }
  }, [showMobileNumberModal])

  const onCloseModal = () => {
    setShowMobileNumberModal(false)
    document.documentElement.style.overflowY = 'scroll'
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onCloseModal()
    }
  }

  const handleContinue = () => {
    if (!validateMobile(mobileNo)) {
      setError(ENTER_VALID_MOBILE_NUMBER)
      return
    }
    onSubmit && onSubmit(mobileNo)
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value.replace(/[^\d]/g, '').slice(0, 10)
    setMobileNo(inputValue)
    if (error) setError('')
  }

  return (
    <div>
      <div className={isMobile ? styles.overlay : styles.desktopOverlay} onClick={handleOverlayClick}>
        <div className={isMobile ? styles.modal : styles.desktopModal} ref={modalRef}>
          <button type="button" className={styles.closeButton} onClick={onCloseModal}>
            &times;
          </button>
          <div className={isMobile ? styles.modalContent : styles.modalDesktopContent}>
            <div className={isMobile ? styles.headerContainer : styles.desktopHeaderContainer}>
              <h1 className={`${styles.header} ${styles.heading}`}>
                {ENTER_INFO_MAPPING[widgetType] || ENTER_INFO_MAPPING.Default}
              </h1>
              {!isHsrpPage && (
                <h2 className={`${styles.header} ${styles.subheading}`}>
                  {DESCRIPTION_MAPPING[widgetType] || DESCRIPTION_MAPPING.Default}
                </h2>
              )}
            </div>
            <div>
              <div className={error ? styles.errorInput : styles.inputContainer}>
                <span>{PHONE_NUMBER_PREFIX}</span>
                <input
                  id="mobileNumber"
                  type="tel"
                  value={mobileNo}
                  onChange={handleInputChange}
                  placeholder={ENTER_MOBILE_NUMBER}
                  inputMode="numeric"
                  className={styles.input}
                />
              </div>
              {error && <p className={styles.error}>{ENTER_VALID_MOBILE_NUMBER}</p>}
            </div>
            <div className={isMobile ? styles.actionSection : styles.desktopActionSection}>
              <button type="button" onClick={handleContinue} className={styles.continueButton}>
                {BUTTON_CTA}
              </button>
              <div className={styles.terms}>
                <Image width={20} height={20} src={MOBILE_NUMBER_LOCK} alt="Loading.." loading="lazy" />
                <p>{PHONE_NUMBER_INFO[widgetType] || PHONE_NUMBER_INFO.Default}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MobileNumberModal
